import React, { useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Icon from 'common/components/Icon';
import Typography from 'common/components/Typography';

import MaxButton from '../MaxButton';
import {
  formatEur,
  formatXmn,
  toMaxPrecision,
  sanitizeEuroInput,
} from './utils';
import styles from './index.module.css';

const SUFFIX = ' EUR';

const clampCursor = (input, maxPos) => {
  if (!input || document.activeElement !== input) return;
  const clampedStart = Math.min(input.selectionStart, maxPos);
  const clampedEnd = Math.min(input.selectionEnd, maxPos);
  if (
    input.selectionStart !== clampedStart ||
    input.selectionEnd !== clampedEnd
  ) {
    input.setSelectionRange(clampedStart, clampedEnd);
  }
};

const AmountInput = ({
  euroAmount,
  xmnRate,
  xmnBalance,
  euroBalance,
  validationError,
  hasAttemptedSubmit,
  onAmountChange,
  onSubmit,
}) => {
  const inputRef = useRef(null);

  const numericEuroAmount = useMemo(
    () => parseFloat(euroAmount) || 0,
    [euroAmount]
  );

  const xmnEquivalent = useMemo(() => {
    if (numericEuroAmount === 0 || xmnRate === 0) return 0;
    const maxEuro = parseFloat(toMaxPrecision(euroBalance));
    return numericEuroAmount === maxEuro
      ? xmnBalance
      : numericEuroAmount / xmnRate;
  }, [numericEuroAmount, xmnRate, euroBalance, xmnBalance]);

  const displayValue = euroAmount ? `${euroAmount}${SUFFIX}` : `0${SUFFIX}`;
  const maxCursorPos = euroAmount.length || 1;

  useLayoutEffect(() => {
    clampCursor(inputRef.current, maxCursorPos);
  });

  const handleInputChange = useCallback(
    (evt) => {
      const sanitizedValue = sanitizeEuroInput(evt.target.value);
      onAmountChange(sanitizedValue);
    },
    [onAmountChange]
  );

  const handleSelect = useCallback(() => {
    clampCursor(inputRef.current, maxCursorPos);
  }, [maxCursorPos]);

  const handleMaxClick = useCallback(() => {
    onAmountChange(toMaxPrecision(euroBalance), true);
    inputRef.current?.focus();
  }, [euroBalance, onAmountChange]);

  const handleWrapperClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleWrapperKeyDown = useCallback((evt) => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      inputRef.current?.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (evt) => {
      if (evt.key === 'Enter') {
        onSubmit();
      }
    },
    [onSubmit]
  );

  const hasValue = euroAmount !== '';

  return (
    <div className={styles.amountSection}>
      <div className={styles.amountCard}>
        <div className={styles.inputRow}>
          <div className={styles.inputWrapper}>
            <div
              className={styles.amountDisplayWrapper}
              onClick={handleWrapperClick}
              onKeyDown={handleWrapperKeyDown}
              role="button"
              tabIndex={-1}
            >
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                className={classNames(styles.amountInput, {
                  [styles.hasValue]: hasValue,
                })}
                value={displayValue}
                onChange={handleInputChange}
                onSelect={handleSelect}
                onKeyDown={handleKeyDown}
                aria-label="Euro amount"
                aria-invalid={hasAttemptedSubmit && validationError !== null}
                data-test="amount-input"
              />
            </div>
            <MaxButton onClick={handleMaxClick} disabled={euroBalance <= 0} />
          </div>
          <div className={styles.cryptoRow}>
            <div className={styles.swapIcon}>
              <Icon.ArrowSwap width={16} height={16} />
            </div>
            <span className={styles.xmnAmount}>{formatXmn(xmnEquivalent)}</span>
          </div>
        </div>
        <span className={styles.balanceText}>
          Balance: {formatXmn(xmnBalance)} | {formatEur(euroBalance)}
        </span>
        {hasAttemptedSubmit && validationError && (
          <span
            className={styles.errorText}
            data-test="amount-validation-error"
          >
            {validationError}
          </span>
        )}
      </div>
      <Typography tag="p" size="xs" color="color-neutral-500">
        Calculations are made at the current rate. Final value depends on market
        conditions at time of transfer.
      </Typography>
    </div>
  );
};

AmountInput.propTypes = {
  euroAmount: PropTypes.string.isRequired,
  xmnRate: PropTypes.number.isRequired,
  xmnBalance: PropTypes.number.isRequired,
  euroBalance: PropTypes.number.isRequired,
  validationError: PropTypes.string,
  hasAttemptedSubmit: PropTypes.bool.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

AmountInput.defaultProps = {
  validationError: null,
};

export default AmountInput;
